import { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  axisAngleDeg,
  controlPointLabel,
  denominatorFromPdfScale,
  fmtNumber,
  fmtScale,
  isAutoControlPoint,
  type GeoControlPoint,
  type GeoCoordsLetters,
  type GeoSettings,
  type GeoSheetLetters,
} from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { Input, Select } from '@shared/ui/Input';
import { IconPlus, IconTrash } from '@shared/ui/icons';
import {
  Chip,
  ChoiceCard,
  ChoiceGrid,
  DataTable,
  ErrorText,
  FieldGrid,
  FieldLabel,
  FieldNote,
  Fieldset,
  Hint,
  IconButton,
  Kpi,
  KpiGrid,
  Muted,
  Notice,
  NumInput,
  Num,
  Panel,
  PanelHead,
  PanelTitle,
  Row,
  SourceTag,
  Stack,
  TableWrap,
  ToneRow,
  useDraft,
} from '@widgets/GeoSchemes';
import { StepShell, useSettingsSaver, useStepDirty, useWizard } from './wizard';
import { SheetPointPicker, type SheetPick } from './SheetPointPicker';
import { AutoBindPanel } from './AutoBindPanel';
import { AxesBindPanel } from './AxesBindPanel';
import { CoordsBindPanel } from './CoordsBindPanel';

import { StructureBindPanel } from './StructureBindPanel';
import { useSheetPreviewUrl, useSheetSnaps } from './sheetPreview';

/**
 * Системы координат объектов компании: МСК-72 и строительная сетка заводских
 * площадок (главный геодезист, 29.10.2026). Московские МСК-50/77 сбивали с
 * толку — их, как и любую другую СК, вписывают в поле.
 */
const CRS_PRESETS = ['МСК-72 (зона 2)', 'Строительная сетка', 'Местная', 'Условная'];
const HEIGHT_PRESETS = ['Балтийская 1977 г.', 'Условная'];
/** Невязка привязки выше этого — предупреждение контроля ГОСТ (ТЗ §5). */
const RMS_WARN_M = 0.05;

type BindingDraft = Omit<GeoSettings['binding'], 'result'>;

const LETTERS: ReadonlyArray<{ code: GeoSheetLetters; title: string; note: string }> = [
  { code: 'auto', title: 'Определить по листу', note: 'по выноскам «X=, Y=» и съёмке' },
  { code: 'north', title: 'X — на север', note: 'как у геодезиста: X — север, Y — восток' },
  {
    code: 'swapped',
    title: 'X — на восток',
    note: 'строительная сетка: «X=» — восток, «Y=» — север',
  },
];

const LETTERS_CHECK: Readonly<Record<GeoCoordsLetters['check'], string>> = {
  geometry: 'понятно по самим выноскам',
  points: 'проверено по съёмке',
  letters: 'буквы N/E однозначны',
  setting: 'как задано здесь',
  unchecked: 'не проверено — проверится по съёмке, когда она будет загружена',
};

/** Что получилось у привязки по выноскам — под выбором букв. */
function coordsLettersText(c: GeoCoordsLetters): string {
  const how = c.swapped ? '«X=» — восток, «Y=» — север' : '«X=» — север, «Y=» — восток';
  return `Лист привязан по выноскам: ${how} (${LETTERS_CHECK[c.check]}).`;
}

const Presets = styled(Row)`
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const PointCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  white-space: nowrap;
`;

const PointId = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-weight: 700;
`;

function nextPointId(points: GeoControlPoint[]): string {
  const max = points.reduce((m, p) => {
    const n = /^Т(\d+)$/.exec(p.id);
    return n ? Math.max(m, Number(n[1])) : m;
  }, 0);
  return `Т${max + 1}`;
}

/** Невязка, м → «12,3 мм». */
function fmtResidualMm(m: number): string {
  return `${fmtNumber(m * 1000, 1)} мм`;
}

function complete(p: GeoControlPoint): boolean {
  return [p.sheet_x, p.sheet_y, p.x, p.y].every((v) => Number.isFinite(v));
}

/**
 * Множители единиц DXF к метрам для привязки «1:1»: сначала по единицам из
 * заголовка, затем метры и миллиметры — заголовок часто врёт (лист, вставленный
 * из PDF в координаты площадки, помечен дюймами, а начерчен в метрах).
 */
function metersPerUnit(units: string): number[] {
  const declared: Record<string, number> = { m: 1, mm: 0.001, cm: 0.01, in: 0.0254 };
  const first = declared[units.toLowerCase()];
  return [...new Set([...(first != null ? [first] : []), 1, 0.001])];
}

/**
 * Доля точек съёмки, лежащих на листе, если лист уже в координатах съёмки с
 * множителем `k`: u листа — Y (восток), v — X (север).
 */
function shareOnSheet(
  ext: readonly [number, number, number, number],
  pts: ReadonlyArray<{ x: number; y: number }>,
  k: number,
): number {
  if (pts.length === 0) return 0;
  const [x0, y0, x1, y1] = ext;
  const inside = pts.filter(
    (p) => p.y >= x0 * k && p.y <= x1 * k && p.x >= y0 * k && p.x <= y1 * k,
  ).length;
  return inside / pts.length;
}

/** Шаг 2. Система координат и высот + привязка листа к МСК (или угол осей). */
export function StepCrs() {
  const { scheme, readOnly } = useWizard();
  const s = scheme.settings;
  const crs = useDraft(s.crs);
  const binding = useDraft<BindingDraft>({
    mode: s.binding.mode,
    control_points: s.binding.control_points,
    axis_angle_deg: s.binding.axis_angle_deg,
  });
  const dirty = crs.dirty || binding.dirty;
  useStepDirty('crs', dirty && !readOnly);
  const saver = useSettingsSaver();
  const preview = useSheetPreviewUrl(scheme);
  const [selected, setSelected] = useState<string | null>(null);
  const [axisA, setAxisA] = useState('');
  const [axisB, setAxisB] = useState('');

  const b = binding.draft;
  // Точки графики листа нужны только тому, кто ставит точки привязки.
  const snaps = useSheetSnaps(scheme, !readOnly && b.mode === 'sheet');
  const points = b.control_points;
  const sheet = scheme.sheet;
  const result = s.binding.result;
  const showResult = b.mode === 'sheet' && result != null && !binding.dirty;
  const residualById = new Map((result?.residuals ?? []).map((r) => [r.id, r]));

  let blockedReason: string | null = null;
  if (crs.draft.name.trim() === '' || crs.draft.height_system.trim() === '')
    blockedReason = 'Укажите систему координат и систему высот.';
  else if (b.mode === 'sheet' && !sheet)
    blockedReason = 'Лист не загружен — выберите другой режим.';
  else if (b.mode === 'sheet' && points.length < 2)
    blockedReason = 'Для привязки нужно минимум 2 точки (лучше 3 и больше).';
  else if (b.mode === 'sheet' && !points.every(complete))
    blockedReason = 'У каждой точки должны быть координаты на листе и X, Y в МСК.';
  else if (b.mode === 'angle' && !Number.isFinite(b.axis_angle_deg))
    blockedReason = 'Укажите угол осей здания.';

  function setPoints(fn: (prev: GeoControlPoint[]) => GeoControlPoint[]) {
    binding.update((prev) => ({ ...prev, control_points: fn(prev.control_points) }));
  }

  /** Точка с листа: координаты уже округлены пикером (с графики — точные). */
  function addPoint(pick: SheetPick): string {
    const id = nextPointId(points);
    setPoints((prev) => [
      ...prev,
      { id, sheet_x: pick.u, sheet_y: pick.v, x: Number.NaN, y: Number.NaN },
    ]);
    setSelected(id);
    return id;
  }

  function patchPoint(id: string, patch: Partial<GeoControlPoint>) {
    setPoints((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }

  const elements = scheme.design.elements;
  const elA = elements.find((e) => e.id === axisA);
  const elB = elements.find((e) => e.id === axisB);
  const computedAngle = elA && elB ? axisAngleDeg(elA, elB) : null;

  // DXF, начерченный прямо в координатах площадки (как у геодезиста: точки
  // съёмки лежат в том же чертеже), привязывается без точек на листе — 1:1.
  const survey = scheme.survey.points;
  const oneToOne = useMemo(() => {
    if (!sheet || sheet.kind !== 'dxf') return null;
    let best: { k: number; share: number } | null = null;
    for (const k of metersPerUnit(sheet.units)) {
      const share = shareOnSheet(sheet.extents, survey, k);
      if (best == null || share > best.share) best = { k, share };
    }
    return best;
  }, [sheet, survey]);

  function bindOneToOne() {
    if (!sheet || !oneToOne) return;
    if (
      points.length > 0 &&
      !window.confirm('Точки привязки заменятся тремя углами листа (привязка 1:1). Продолжить?')
    )
      return;
    const [x0, y0, x1, y1] = sheet.extents;
    const k = oneToOne.k;
    setPoints(() => [
      { id: 'Т1', sheet_x: x0, sheet_y: y0, x: y0 * k, y: x0 * k },
      { id: 'Т2', sheet_x: x1, sheet_y: y0, x: y0 * k, y: x1 * k },
      { id: 'Т3', sheet_x: x1, sheet_y: y1, x: y1 * k, y: x1 * k },
    ]);
    setSelected(null);
  }

  const pdfDenominator =
    showResult && sheet?.units.toLowerCase() === 'pt' && result
      ? denominatorFromPdfScale(result.scale)
      : null;

  return (
    <StepShell
      n={2}
      lead="Схема строится в местной системе координат (МСК) и Балтийской системе высот, если проект не требует иного. Подпись осей: X — на север, Y — на восток."
      save={{
        dirty: dirty && !readOnly,
        busy: saver.busy,
        error: saver.error,
        blockedReason,
        onSave: async () => {
          const ok = await saver.save((cur) => ({
            ...cur,
            crs: {
              name: crs.draft.name.trim(),
              height_system: crs.draft.height_system.trim(),
              letters: crs.draft.letters ?? 'auto',
            },
            // Сводки привязки (по конструкции, по выноскам) — как на сервере:
            // он снимет их сам, если набор точек правили.
            binding: { ...cur.binding, ...b },
          }));
          // Принять ответ сервера как есть (он нормализует и считает невязки).
          if (ok) {
            crs.reset();
            binding.reset();
          }
          return ok;
        },
        onReset: () => {
          crs.reset();
          binding.reset();
        },
      }}
    >
      <Stack $gap={4}>
        <Panel>
          <PanelHead>
            <PanelTitle>Системы координат и высот</PanelTitle>
          </PanelHead>
          <Fieldset disabled={readOnly}>
            <FieldGrid $min={240}>
              <FieldLabel>
                Система координат
                <Input
                  value={crs.draft.name}
                  onChange={(e) => crs.set({ ...crs.draft, name: e.target.value })}
                  placeholder="МСК-72 (зона 2)"
                />
                <Presets $gap={1.5}>
                  {CRS_PRESETS.map((p) => (
                    <Chip
                      key={p}
                      type="button"
                      $active={crs.draft.name === p}
                      aria-pressed={crs.draft.name === p}
                      onClick={() => crs.set({ ...crs.draft, name: p })}
                    >
                      {p}
                    </Chip>
                  ))}
                </Presets>
              </FieldLabel>
              <FieldLabel>
                Система высот
                <Input
                  value={crs.draft.height_system}
                  onChange={(e) => crs.set({ ...crs.draft, height_system: e.target.value })}
                  placeholder="Балтийская 1977 г."
                />
                <Presets $gap={1.5}>
                  {HEIGHT_PRESETS.map((p) => (
                    <Chip
                      key={p}
                      type="button"
                      $active={crs.draft.height_system === p}
                      aria-pressed={crs.draft.height_system === p}
                      onClick={() => crs.set({ ...crs.draft, height_system: p })}
                    >
                      {p}
                    </Chip>
                  ))}
                </Presets>
              </FieldLabel>
            </FieldGrid>
          </Fieldset>
        </Panel>

        <Panel aria-labelledby="geo-letters-title">
          <PanelHead>
            <PanelTitle id="geo-letters-title">Как подписаны X и Y на листах проекта</PanelTitle>
          </PanelHead>
          <Stack $gap={3}>
            <Hint>
              У каждого проекта по-своему: у строительной сетки «X=» на выносках бывает востоком
              (так на листах КЖ2 СИБУРа). От этого зависит привязка листа по выноскам «X=…, Y=…» и
              буквы в каталоге координат схемы.
            </Hint>
            <Fieldset disabled={readOnly}>
              <ChoiceGrid $min={200} role="radiogroup" aria-labelledby="geo-letters-title">
                {LETTERS.map((o) => (
                  <ChoiceCard
                    key={o.code}
                    name="geo-letters"
                    checked={(crs.draft.letters ?? 'auto') === o.code}
                    onSelect={() => crs.set({ ...crs.draft, letters: o.code })}
                    title={o.title}
                  >
                    {o.note}
                  </ChoiceCard>
                ))}
              </ChoiceGrid>
            </Fieldset>
            {s.binding.coords && <Muted>{coordsLettersText(s.binding.coords)}</Muted>}
          </Stack>
        </Panel>

        <Panel>
          <PanelHead>
            <PanelTitle>Привязка чертежа к МСК</PanelTitle>
          </PanelHead>
          <Stack $gap={4}>
            <Fieldset disabled={readOnly}>
              <ChoiceGrid $min={200} role="radiogroup" aria-label="Способ привязки">
                <ChoiceCard
                  name="geo-binding-mode"
                  checked={b.mode === 'sheet'}
                  disabled={!sheet}
                  onSelect={() => binding.set({ ...b, mode: 'sheet' })}
                  title="По точкам листа"
                >
                  {sheet
                    ? 'Отметьте на листе 2–3+ точки с известными X, Y — лист ляжет в МСК.'
                    : 'Нужен проектный лист (шаг 1).'}
                </ChoiceCard>
                <ChoiceCard
                  name="geo-binding-mode"
                  checked={b.mode === 'angle'}
                  onSelect={() => binding.set({ ...b, mode: 'angle' })}
                  title="Угол осей здания"
                >
                  Без подложки: оси чертежа повёрнуты на заданный угол.
                </ChoiceCard>
                <ChoiceCard
                  name="geo-binding-mode"
                  checked={b.mode === 'none'}
                  onSelect={() => binding.set({ ...b, mode: 'none' })}
                  title="Без поворота"
                >
                  Оси здания совпадают с осями МСК.
                </ChoiceCard>
              </ChoiceGrid>
            </Fieldset>

            {b.mode === 'sheet' && sheet && (
              <>
                {oneToOne && !readOnly && (
                  <Notice $tone={oneToOne.share >= 0.5 ? 'success' : 'info'} role="status">
                    <span>
                      {oneToOne.share >= 0.5
                        ? `Точки съёмки лежат на листе в его координатах (${Math.round(
                            oneToOne.share * 100,
                          )} %) — чертёж уже в МСК: привяжите 1:1, без точек на листе.`
                        : 'Чертёж DXF уже в координатах МСК, как съёмка? Привяжите 1:1 — без точек на листе.'}
                    </span>
                    <div>
                      <Button type="button" $variant="secondary" $size="sm" onClick={bindOneToOne}>
                        {oneToOne.k === 1
                          ? 'Привязать 1:1'
                          : `Привязать 1:1 (единицы листа × ${fmtNumber(oneToOne.k, 4)} м)`}
                      </Button>
                    </div>
                  </Notice>
                )}
                <StructureBindPanel
                  draftPoints={points.length}
                  dirty={binding.dirty}
                  onDone={binding.reset}
                />
                <CoordsBindPanel
                  draftPoints={points.length}
                  dirty={binding.dirty}
                  onDone={binding.reset}
                />
                <AxesBindPanel
                  draftPoints={points.length}
                  dirty={binding.dirty}
                  onDone={binding.reset}
                />
                <AutoBindPanel
                  draftPoints={points.length}
                  dirty={binding.dirty}
                  onDone={binding.reset}
                />
                <SheetPointPicker
                  sheet={sheet}
                  url={preview.url}
                  loadError={preview.isError}
                  snaps={snaps}
                  points={points}
                  selectedId={selected}
                  editable={!readOnly}
                  onAdd={addPoint}
                  onMove={(id, pick) => patchPoint(id, { sheet_x: pick.u, sheet_y: pick.v })}
                  onSelect={setSelected}
                />
                <Fieldset disabled={readOnly}>
                  <Stack $gap={3}>
                    <Row $gap={3}>
                      <Button
                        type="button"
                        $variant="secondary"
                        $size="sm"
                        onClick={() => {
                          const id = nextPointId(points);
                          setPoints((prev) => [
                            ...prev,
                            {
                              id,
                              sheet_x: Number.NaN,
                              sheet_y: Number.NaN,
                              x: Number.NaN,
                              y: Number.NaN,
                            },
                          ]);
                          setSelected(id);
                        }}
                      >
                        <IconPlus size={16} /> Добавить точку вручную
                      </Button>
                      <Muted>
                        Точек: {points.length}. Минимум 2, для контроля невязок — 3 и больше.
                      </Muted>
                    </Row>
                    {points.length > 0 && (
                      <TableWrap>
                        <DataTable>
                          <thead>
                            <tr>
                              <th>Точка</th>
                              <th className="num">Лист, u</th>
                              <th className="num">Лист, v</th>
                              <th className="num">X (север), м</th>
                              <th className="num">Y (восток), м</th>
                              <th className="num">Невязка, мм</th>
                              <th aria-label="Действия" />
                            </tr>
                          </thead>
                          <tbody>
                            {points.map((p) => {
                              const r = showResult ? residualById.get(p.id) : undefined;
                              const res = r ? Math.hypot(r.dx_m, r.dy_m) : null;
                              const name = controlPointLabel(p.id);
                              return (
                                <ToneRow
                                  key={p.id}
                                  $tone={p.id === selected ? 'warning' : undefined}
                                  onClick={() => setSelected(p.id)}
                                >
                                  <td>
                                    <PointCell>
                                      <PointId>{name}</PointId>
                                      {isAutoControlPoint(p.id) && (
                                        <SourceTag title="Поставлена автопривязкой по номеру элемента">
                                          авто
                                        </SourceTag>
                                      )}
                                    </PointCell>
                                  </td>
                                  <td className="num">
                                    <NumInput
                                      variant="cell"
                                      value={p.sheet_x}
                                      onValue={(v) =>
                                        patchPoint(p.id, { sheet_x: v ?? Number.NaN })
                                      }
                                      aria-label={`${name}: координата листа u`}
                                    />
                                  </td>
                                  <td className="num">
                                    <NumInput
                                      variant="cell"
                                      value={p.sheet_y}
                                      onValue={(v) =>
                                        patchPoint(p.id, { sheet_y: v ?? Number.NaN })
                                      }
                                      aria-label={`${name}: координата листа v`}
                                    />
                                  </td>
                                  <td className="num">
                                    <NumInput
                                      variant="cell"
                                      value={p.x}
                                      onValue={(v) => patchPoint(p.id, { x: v ?? Number.NaN })}
                                      aria-label={`${name}: X, север, м`}
                                    />
                                  </td>
                                  <td className="num">
                                    <NumInput
                                      variant="cell"
                                      value={p.y}
                                      onValue={(v) => patchPoint(p.id, { y: v ?? Number.NaN })}
                                      aria-label={`${name}: Y, восток, м`}
                                    />
                                  </td>
                                  <td className="num">
                                    {res == null ? (
                                      '—'
                                    ) : (
                                      <Num
                                        $tone={res > RMS_WARN_M ? 'danger' : undefined}
                                        $strong={res > RMS_WARN_M}
                                      >
                                        {fmtNumber(res * 1000, 1)}
                                      </Num>
                                    )}
                                  </td>
                                  <td>
                                    <IconButton
                                      type="button"
                                      $danger
                                      aria-label={`Удалить точку ${name}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPoints((prev) => prev.filter((x) => x.id !== p.id));
                                      }}
                                    >
                                      <IconTrash size={16} />
                                    </IconButton>
                                  </td>
                                </ToneRow>
                              );
                            })}
                          </tbody>
                        </DataTable>
                      </TableWrap>
                    )}
                  </Stack>
                </Fieldset>
                {showResult && result ? (
                  <Stack $gap={2}>
                    <KpiGrid>
                      <Kpi value={result.residuals.length} label="точек привязки" />
                      <Kpi
                        value={fmtResidualMm(result.rms_m)}
                        label="СКО привязки (RMS)"
                        tone={result.rms_m > RMS_WARN_M ? 'warning' : 'success'}
                      />
                      <Kpi value={`${fmtNumber(result.rotation_deg, 4)}°`} label="поворот листа" />
                      <Kpi
                        value={
                          pdfDenominator
                            ? `≈ ${fmtScale(pdfDenominator)}`
                            : fmtNumber(result.scale, 6)
                        }
                        label={
                          pdfDenominator
                            ? 'масштаб листа по привязке'
                            : 'масштаб, м на единицу листа'
                        }
                      />
                    </KpiGrid>
                    {result.rms_m > RMS_WARN_M && (
                      <Notice $tone="warning" role="status">
                        СКО больше 50 мм — проверьте точки с наибольшей невязкой: возможно,
                        перепутаны X и Y или точка поставлена не на тот узел.
                      </Notice>
                    )}
                  </Stack>
                ) : (
                  b.mode === 'sheet' &&
                  points.length >= 2 && (
                    <Hint>Сохраните шаг — сервер решит привязку и покажет невязки по точкам.</Hint>
                  )
                )}
              </>
            )}

            {b.mode === 'sheet' && !sheet && (
              <Notice $tone="warning" role="status">
                Проектный лист убран — выберите «Угол осей здания» или загрузите лист на шаге 1.
              </Notice>
            )}

            {b.mode === 'angle' && (
              <Fieldset disabled={readOnly}>
                <Stack $gap={3}>
                  <FieldGrid $min={220}>
                    <FieldLabel>
                      Угол осей здания α, °
                      <NumInput
                        value={b.axis_angle_deg}
                        onValue={(v) => binding.set({ ...b, axis_angle_deg: v ?? Number.NaN })}
                        placeholder="0"
                      />
                      <FieldNote>
                        От направления на восток (ось Y МСК) против часовой стрелки; 0° — оси здания
                        параллельны осям МСК.
                      </FieldNote>
                    </FieldLabel>
                  </FieldGrid>
                  {elements.length >= 2 && (
                    <Stack $gap={2}>
                      <Hint>Или вычислите по двум проектным точкам одной оси:</Hint>
                      <Row $gap={2} $align="flex-end">
                        <FieldLabel>
                          Начало оси
                          <Select value={axisA} onChange={(e) => setAxisA(e.target.value)}>
                            <option value="">—</option>
                            {elements.map((el) => (
                              <option key={el.id} value={el.id}>
                                {el.label}
                              </option>
                            ))}
                          </Select>
                        </FieldLabel>
                        <FieldLabel>
                          Конец оси
                          <Select value={axisB} onChange={(e) => setAxisB(e.target.value)}>
                            <option value="">—</option>
                            {elements.map((el) => (
                              <option key={el.id} value={el.id}>
                                {el.label}
                              </option>
                            ))}
                          </Select>
                        </FieldLabel>
                        <Button
                          type="button"
                          $variant="secondary"
                          disabled={computedAngle == null}
                          onClick={() => {
                            if (computedAngle != null)
                              binding.set({
                                ...b,
                                axis_angle_deg: Math.round(computedAngle * 1e4) / 1e4,
                              });
                          }}
                        >
                          Взять угол
                          {computedAngle != null ? ` ${fmtNumber(computedAngle, 4)}°` : ''}
                        </Button>
                      </Row>
                    </Stack>
                  )}
                </Stack>
              </Fieldset>
            )}

            {b.mode === 'none' && (
              <Hint>
                Чертёж строится в осях МСК без поворота: ось x листа — на восток. Подходит, когда
                разбивочные оси здания идут по сетке МСК.
              </Hint>
            )}
            {readOnly && blockedReason && <ErrorText>{blockedReason}</ErrorText>}
          </Stack>
        </Panel>
      </Stack>
    </StepShell>
  );
}
